/**
 * GUSTA CUSTOMIZATION: PRODUCT NOTICE VARIANT VISIBILITY
 *
 * Handles two durable variant-aware behaviours:
 * 1. hides shipping-related purchase notices for pickup-only variants;
 * 2. shows variant-level custom.product_notices only for the selected variant.
 *
 * Warehouse wraps the product form in <product-rerender> and may replace its
 * children after a variant change. Re-synchronize after those child updates so
 * notice visibility follows the currently selected variant without a refresh.
 */
(function () {
  'use strict';

  if (window.gustaProductNoticesInitialized) {
    return;
  }

  window.gustaProductNoticesInitialized = true;

  var DATA_SELECTOR = 'script[data-gusta-preorder-variants]';
  var NOTICE_SELECTOR = '[data-gusta-hide-for-pickup-only]';
  var VARIANT_NOTICE_SELECTOR = '[data-gusta-variant-notice]';
  var CONTAINER_SELECTOR = '.gusta-product-notices--purchase';
  var ITEM_SELECTOR = '.gusta-product-notice__item';
  var observedRerenders = new WeakSet();

  function parseData(script) {
    try {
      return JSON.parse(script.textContent || '{}');
    } catch (error) {
      console.warn('Gusta product notices: invalid variant data.', error);
      return null;
    }
  }

  function findVariant(data, variantId) {
    if (!data || !Array.isArray(data.variants) || variantId == null) {
      return null;
    }

    return data.variants.find(function (variant) {
      return String(variant.id) === String(variantId);
    }) || null;
  }

  function getCurrentVariantId(form) {
    var variantIdInput = form && form.elements && form.elements.namedItem('id');
    return variantIdInput && variantIdInput.value;
  }

  function getProductRoot(form) {
    return (
      (form && form.closest('product-rerender')) ||
      (form && form.closest('.shopify-section')) ||
      document
    );
  }

  function updateContainerVisibility(root) {
    root.querySelectorAll(CONTAINER_SELECTOR).forEach(function (container) {
      var items = Array.prototype.slice.call(container.querySelectorAll(ITEM_SELECTOR));
      var hasVisibleItem = items.some(function (item) {
        return item.hidden !== true;
      });

      container.hidden = !hasVisibleItem;

      if (hasVisibleItem) {
        container.removeAttribute('aria-hidden');
      } else {
        container.setAttribute('aria-hidden', 'true');
      }
    });
  }

  function synchronizePickupOnlyNotices(root, variant) {
    if (!variant) {
      return;
    }

    var hideShippingNotices = variant.pickupOnly === true;

    root.querySelectorAll(NOTICE_SELECTOR).forEach(function (notice) {
      notice.hidden = hideShippingNotices;

      if (hideShippingNotices) {
        notice.setAttribute('aria-hidden', 'true');
      } else {
        notice.removeAttribute('aria-hidden');
      }
    });
  }

  function synchronizeVariantNotices(root, variantId) {
    if (variantId == null) {
      return;
    }

    root.querySelectorAll(VARIANT_NOTICE_SELECTOR).forEach(function (notice) {
      var isSelected = String(notice.getAttribute('data-gusta-variant-id')) === String(variantId);
      notice.hidden = !isSelected;

      if (isSelected) {
        notice.removeAttribute('aria-hidden');
      } else {
        notice.setAttribute('aria-hidden', 'true');
      }
    });
  }

  function observeProductRerender(form) {
    var rerender = form && form.closest && form.closest('product-rerender');

    if (!rerender || observedRerenders.has(rerender)) {
      return;
    }

    observedRerenders.add(rerender);

    var scheduled = false;
    var observer = new MutationObserver(function () {
      if (scheduled) {
        return;
      }

      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        initializeAll(rerender);
      });
    });

    observer.observe(rerender, { childList: true, subtree: true });
  }

  function synchronizeForm(form, variantId) {
    if (!form) {
      return;
    }

    var script = form.querySelector(DATA_SELECTOR);
    var data = script && parseData(script);
    var resolvedVariantId = variantId || getCurrentVariantId(form);
    var variant = findVariant(data, resolvedVariantId);
    var root = getProductRoot(form);

    synchronizePickupOnlyNotices(root, variant);
    synchronizeVariantNotices(root, resolvedVariantId);
    updateContainerVisibility(root);
    observeProductRerender(form);
  }

  function addForm(forms, form) {
    if (form && forms.indexOf(form) === -1) {
      forms.push(form);
    }
  }

  function initializeAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var forms = [];

    if (scope.matches && scope.matches(DATA_SELECTOR)) {
      addForm(forms, document.getElementById(scope.dataset.formId) || scope.closest('form'));
    }

    scope.querySelectorAll(DATA_SELECTOR).forEach(function (script) {
      addForm(forms, document.getElementById(script.dataset.formId) || script.closest('form'));
    });

    scope.querySelectorAll(VARIANT_NOTICE_SELECTOR).forEach(function (notice) {
      var productRoot = notice.closest('product-rerender') || notice.closest('.shopify-section') || document;
      addForm(forms, productRoot.querySelector('form[action*="/cart/add"]'));
    });

    scope.querySelectorAll(NOTICE_SELECTOR).forEach(function (notice) {
      var productRoot = notice.closest('product-rerender') || notice.closest('.shopify-section') || document;
      addForm(forms, productRoot.querySelector('form[action*="/cart/add"]'));
    });

    forms.forEach(function (form) {
      synchronizeForm(form, getCurrentVariantId(form));
    });
  }

  document.addEventListener('variant:changed', function (event) {
    var detail = event.detail || {};
    var form = null;

    if (detail.formId) {
      form = document.getElementById(detail.formId);
    }

    if (!form && event.target && event.target.closest) {
      form = event.target.closest('form');
    }

    if (!form) {
      return;
    }

    var variantId = detail.variant && detail.variant.id;
    synchronizeForm(form, variantId || getCurrentVariantId(form));
  });

  document.addEventListener('change', function (event) {
    var control = event.target;

    if (!control || !control.dataset || !control.dataset.optionPosition) {
      return;
    }

    var form = document.getElementById(control.getAttribute('form')) || control.closest('form');

    if (!form) {
      return;
    }

    window.requestAnimationFrame(function () {
      synchronizeForm(form, getCurrentVariantId(form));
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initializeAll(document);
    });
  } else {
    initializeAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initializeAll(event.target);
  });
})();
